/**
 * device/{userId}/{deviceId}/status 接收侧解析：先读 type，再分支处理。
 * 输出统一信封 { type, deviceId, timestamp, data } 或兼容旧格式的 payload，供下游 AudioManager 消费。
 */

import { isPmr171FactoryCalibrationType } from '../pmr171FactoryCalibrationMqtt';
import { isPmr171ChannelType } from '../pmr171ChannelMqtt';

export function normalizeDeviceStatusMessageType(type: unknown): string {
  if (type == null) return '';
  return String(type).trim().toUpperCase();
}

export interface DeviceStatusMqttEnvelope {
  type: string;
  deviceId: string;
  timestamp: number;
  data: Record<string, unknown>;
}

function resolveTimestamp(
  raw: Record<string, unknown>,
  data?: Record<string, unknown>,
): number {
  for (const source of [raw, data ?? {}]) {
    const t = source.timestamp;
    if (typeof t === 'number' && Number.isFinite(t)) return t;
  }
  return Date.now();
}

function asDataRecord(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function buildTypedEnvelope(
  type: string,
  raw: Record<string, unknown>,
  deviceId: string,
): DeviceStatusMqttEnvelope {
  const data = asDataRecord(raw.data);
  return {
    type,
    deviceId: String(raw.deviceId ?? data.deviceId ?? deviceId),
    timestamp: resolveTimestamp(raw, data),
    data: Object.keys(data).length > 0 ? data : asDataRecord(raw),
  };
}

function parse0x0bEnvelope(
  raw: Record<string, unknown>,
  deviceId: string,
): DeviceStatusMqttEnvelope {
  const nested = asDataRecord(raw.data);
  /** 与 0X2D 一致：data 为空时回退到整包，兼容频率写在顶层的 0X0B */
  const data = Object.keys(nested).length > 0 ? nested : asDataRecord(raw);
  return {
    type: '0X0B',
    deviceId: String(raw.deviceId ?? data.deviceId ?? deviceId),
    timestamp: resolveTimestamp(raw, data),
    data,
  };
}

/** 从 MQTT status data 读取整数 Hz（兼容 number / 数字字符串） */
export function readMqttFrequencyHz(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.round(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const n = Number(trimmed);
    if (Number.isFinite(n)) return Math.round(n);
  }
  return undefined;
}

export function hasMqttFrequencyField(
  record: unknown,
  key: 'vfoAFrequency' | 'vfoBFrequency',
): boolean {
  if (!record || typeof record !== 'object') return false;
  return readMqttFrequencyHz((record as Record<string, unknown>)[key]) !== undefined;
}

function parseLegacyPayload(
  raw: Record<string, unknown>,
  deviceId: string,
  topicDeviceId: string,
): { deviceId: string; payload: unknown } {
  if (typeof raw.status === 'string' && raw.type == null && raw.data == null) {
    return { deviceId: topicDeviceId, payload: raw.status };
  }

  const data = raw.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return { deviceId, payload: raw };
  }

  return { deviceId, payload: raw };
}

/**
 * 解析 device status MQTT JSON，按 type 路由后返回 deviceId 与下游 payload。
 */
export function parseDeviceStatusMqttMessage(
  raw: unknown,
  topicDeviceId: string,
): { deviceId: string; payload: unknown } {
  if (!raw || typeof raw !== 'object') {
    return { deviceId: topicDeviceId, payload: raw };
  }

  const obj = raw as Record<string, unknown>;
  const msgType = normalizeDeviceStatusMessageType(obj.type);
  const deviceId = String(obj.deviceId ?? topicDeviceId).trim() || topicDeviceId;

  switch (msgType) {
    case '0X0B':
      return { deviceId, payload: parse0x0bEnvelope(obj, deviceId) };
    case '0X09':
      return { deviceId, payload: buildTypedEnvelope(msgType, obj, deviceId) };
    case '0X2E':
    case '0X2D':
    case '0X40':
    case '0X41':
    case '0X42':
    case 'FACTORY_READ_ALL_DONE':
    case 'FACTORY_READ_BAND_DONE':
      return { deviceId, payload: buildTypedEnvelope(msgType, obj, deviceId) };
    default:
      if (isPmr171FactoryCalibrationType(msgType) || isPmr171ChannelType(msgType)) {
        return { deviceId, payload: buildTypedEnvelope(msgType, obj, deviceId) };
      }
      return parseLegacyPayload(obj, deviceId, topicDeviceId);
  }
}
